import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, SquarePen, StickyNote } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import { upperFirst } from "helpers/string";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import { useCustomTableSchema } from "hooks/useCustomTableSchema";
import { useDynamicDetailRows } from "hooks/useDynamicDetailRows";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import CustomTableRowService from "services/CustomTableRow";

import CustomTableRowInfoPanel from "./CustomTableRowInfoPanel";
import { rowDisplayValue } from "./helpers";

const CustomTableRowDetail = () => {
  const { slug, rowId } = useParams();
  const canRead = useAuthorized([ACL_RELATIONSHIPS.customTable.readData]);
  const canWrite = useAuthorized([ACL_RELATIONSHIPS.customTable.writeData]);

  const modelType = `CustomTable:${slug}`;

  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const showDisabledTags = useShowDisabledTags();

  const {
    schema,
    isFetched: isSchemaFetched,
    isError: isSchemaError,
  } = useCustomTableSchema(slug);
  const {
    data: row,
    isFetched: isRowFetched,
    isError: isRowError,
  } = useQuery({
    queryKey: ["custom-table-row", slug, rowId],
    queryFn: () => CustomTableRowService.get(slug, rowId),
    enabled: !!slug && !!rowId,
    retry: false,
  });

  // Per-table grant on the schema DTO is authoritative; fall back to the coarse
  // ACL only when it's absent.
  const perTableCan = schema?.current_user_can ?? null;
  const effectiveCanWrite = perTableCan ? perTableCan.write : canWrite;

  const detailRows = useDynamicDetailRows(schema, row);
  const { linkedTabs, isLoading: isLoadingLinkedTabs } = useLinkedTables(
    modelType,
    rowId
  );
  const tabs = useAppTabs({
    basePath: `/custom_tables/${slug}/${rowId}`,
    defaultTab: "activityLog",
  });

  // The system row-tags step aside when the table has its own ``tags`` column.
  const supportsTags = !(schema?.columns ?? []).some(
    c => c.field_id === "tags"
  );

  const refreshRow = () =>
    queryClient.invalidateQueries({
      queryKey: ["custom-table-row", slug, rowId],
    });

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog modelType="CustomTable" slug={slug} modelId={rowId} />
        ),
      },
      {
        key: "notes",
        label: "Notes",
        icon: StickyNote,
        content: (
          <NotesView modelType="CustomTable" slug={slug} modelId={rowId} />
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, rowId));
    return items;
  }, [slug, rowId, linkedTabs]);

  if (!isSchemaFetched || !isRowFetched) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        <Spinner />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        <p>You don&apos;t have permission to view this table&apos;s data.</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  if (isSchemaError || isRowError) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        <p>Couldn&apos;t load this row. Please try again.</p>
        <Link to={`/custom_tables/${slug}`}>Back to table</Link>
      </div>
    );
  }

  if (!schema || !row) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        <p>Row not found.</p>
        <Link to={`/custom_tables/${slug}`}>Back to table</Link>
      </div>
    );
  }

  const singular = schema.singular_name?.trim() || schema.display_name;
  // The display-column value names the row when the table defines one;
  // generic chrome falls back to the singular noun.
  const rowName = rowDisplayValue(schema, row) || singular;
  const tableLabel = upperFirst(schema.display_name ?? "");

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      sectionsLoading={isLoadingLinkedTabs}
      header={tableLabel}
      breadcrumb={
        <DetailBreadcrumb
          parentTo={`/custom_tables/${slug}`}
          parentLabel={`All ${tableLabel}`}
          current={rowName}
        />
      }
      title={rowName}
      titleActions={
        <>
          <Field orientation="horizontal" className="tw:w-auto">
            <Switch
              id="show-empty-fields"
              checked={showEmptyFields}
              onCheckedChange={checked => setShowEmptyFields(checked)}
            />
            <FieldLabel
              htmlFor="show-empty-fields"
              className="tw:text-xs tw:font-normal"
            >
              Empty fields
            </FieldLabel>
          </Field>
          {effectiveCanWrite && (
            <Link
              to={`/custom_tables/${slug}/${rowId}/edit`}
              aria-label={`Edit ${singular}`}
              // Semantic UI's unlayered `a` color beats Tailwind's layered
              // utilities, so only an important utility matches the buttons.
              className={cn(
                buttonVariants({ variant: "outline", size: "sm" }),
                "tw:text-foreground!"
              )}
            >
              <SquarePen data-icon="inline-start" />
              Edit
            </Link>
          )}
        </>
      }
      info={
        <CustomTableRowInfoPanel
          row={row}
          detailRows={detailRows}
          showEmptyFields={showEmptyFields}
          supportsTags={supportsTags}
          modelType={modelType}
          rowId={rowId}
          showDisabledTags={showDisabledTags}
          onUpdate={refreshRow}
        />
      }
      infoLabel={`${singular} Details`}
    />
  );
};

export default CustomTableRowDetail;
